"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PROBLEM_CONTENT_TYPE = exports.problemDetailsSchema = exports.validationProblemSchema = exports.problemTypes = exports.PROBLEM_TYPE_BASE = void 0;
/**
 * RFC 9457 problem details.
 *
 * Section 13: errors use problem details with a stable `type` URI per failure
 * class, and every response carries a `correlationId`, successful or not,
 * "because that identifier is the only thing a customer can read back to us
 * over the phone".
 *
 * This lives in shared/contracts because all three frontends render these and
 * the backend produces them. It is a wire type. The domain does not use it:
 * `core` is forbidden from importing this package precisely so that a public
 * error shape cannot quietly become a domain type.
 */
const zod_1 = require("zod");
/**
 * Type URIs are stable identifiers, not links we promise to serve. Keeping them
 * under one base means a client can match on prefix, and changing the docs host
 * later does not change the identifiers.
 */
exports.PROBLEM_TYPE_BASE = 'https://ddds.iqzsystems.com/problems';
exports.problemTypes = {
    /** Fallback. A client should treat this as "something went wrong, retry later". */
    internal: `${exports.PROBLEM_TYPE_BASE}/internal-error`,
    validation: `${exports.PROBLEM_TYPE_BASE}/validation-failed`,
    notFound: `${exports.PROBLEM_TYPE_BASE}/not-found`,
    unauthenticated: `${exports.PROBLEM_TYPE_BASE}/unauthenticated`,
    forbidden: `${exports.PROBLEM_TYPE_BASE}/forbidden`,
    conflict: `${exports.PROBLEM_TYPE_BASE}/conflict`,
    rateLimited: `${exports.PROBLEM_TYPE_BASE}/rate-limited`,
    notImplemented: `${exports.PROBLEM_TYPE_BASE}/not-implemented`,
    /**
     * The four the upload surface needs (section 09), and none of them is an
     * internal error.
     *
     * Added because every one of these was falling through to `internal` - which
     * tells a client to retry later, when in three of the four cases retrying the
     * same request will fail identically forever. Section 13 asks for a stable
     * type URI per failure class, and a class that says "something went wrong" for
     * a request that was simply too large is not stable, it is wrong.
     */
    gone: `${exports.PROBLEM_TYPE_BASE}/gone`,
    preconditionFailed: `${exports.PROBLEM_TYPE_BASE}/precondition-failed`,
    payloadTooLarge: `${exports.PROBLEM_TYPE_BASE}/payload-too-large`,
    unsupportedMediaType: `${exports.PROBLEM_TYPE_BASE}/unsupported-media-type`,
};
/**
 * One invalid field. `path` is dotted rather than an array so it can be shown
 * to a person without further formatting.
 */
exports.validationProblemSchema = zod_1.z.object({
    path: zod_1.z.string(),
    message: zod_1.z.string(),
});
/**
 * Not strict. RFC 9457 explicitly allows extension members, and rejecting an
 * unknown one would make adding a field a breaking change for every client.
 * This is the opposite of the config schema, where strictness is the point.
 */
exports.problemDetailsSchema = zod_1.z.object({
    /** Stable identifier for the failure class. */
    type: zod_1.z.string(),
    /** Short, human-readable, and the same for every occurrence of this type. */
    title: zod_1.z.string(),
    /** HTTP status, repeated in the body so a logged payload is self-contained. */
    status: zod_1.z.number().int().min(100).max(599),
    /** Specific to this occurrence. Never contains a token or an email address. */
    detail: zod_1.z.string().optional(),
    /** The request path. Credential-bearing segments are redacted before this is set. */
    instance: zod_1.z.string().optional(),
    /** Present on every response. The one thing worth reading down a phone line. */
    correlationId: zod_1.z.string(),
    /** Present when `type` is validation-failed. */
    errors: zod_1.z.array(exports.validationProblemSchema).optional(),
});
/** The media type RFC 9457 requires. Not `application/json`. */
exports.PROBLEM_CONTENT_TYPE = 'application/problem+json';
//# sourceMappingURL=problem-details.js.map